import CheckCircleIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorIcon from "@mui/icons-material/ErrorOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import WarningIcon from "@mui/icons-material/WarningAmberOutlined";
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	Paper,
	Radio,
	RadioGroup,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import { addDays, endOfWeek, format, startOfWeek, subDays } from "date-fns";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { productionPlanApi } from "../../../../services/production-plan-api";
import {
	GhostButton,
	planner,
	PrimaryButton, // eslint-disable-line sort-imports
	staffingColor,
	staffingSoft,
	StaffingState,
} from "../../planner-ui";
import { ManufacturingOrder } from "../utils/calculations";

// Outlined count chip in the status ramp — replaces MUI's success/warning
// palette so the dialog reads in Wilya status colors (design system rule 7).
const statusChipSx = (state: StaffingState) => ({
	...planner.type.metaStrong,
	borderRadius: `${planner.radiusSm}px`,
	borderColor: `${staffingColor(state)}55`,
	bgcolor: staffingSoft(state),
	color: staffingColor(state),
	"& .MuiChip-icon": { color: staffingColor(state) },
});

// Machine data inside the preview tables: times, quantities, dates.
const monoCellSx = {
	...planner.type.meta,
	fontFamily: planner.fontMono,
	color: planner.inkSecondary,
	fontVariantNumeric: "tabular-nums" as const,
};

interface CopyWeekPreviewDialogProps {
	open: boolean
	onClose: () => void
	onSuccess: (message: string) => void
	onError: (message: string) => void
	weekStartDate: string
	currentWeekOrders: ManufacturingOrder[]
	onOrdersUpdated: () => void
}

interface ConflictItem {
	sourceOrder: ManufacturingOrder
	targetOrder: ManufacturingOrder
	action: "keep" | "replace"
}

interface PreviewState {
	loading: boolean
	sourceOrders: ManufacturingOrder[]
	nonConflictingOrders: ManufacturingOrder[]
	conflicts: ConflictItem[]
}

// Check if two time ranges overlap
const doTimesOverlap = (
	start1: Date,
	end1: Date | null,
	start2: Date,
	end2: Date | null
): boolean => {
	// If no end time, assume 1 hour duration
	const effectiveEnd1 = end1 || addDays(start1, 0);
	const effectiveEnd2 = end2 || addDays(start2, 0);

	// Check same day and workcenter - that's a conflict for manufacturing
	const sameDay =
		start1.getFullYear() === start2.getFullYear() &&
		start1.getMonth() === start2.getMonth() &&
		start1.getDate() === start2.getDate();

	return sameDay;
};

const CopyWeekPreviewDialog: React.FC<CopyWeekPreviewDialogProps> = ({
	open,
	onClose,
	onSuccess,
	onError,
	weekStartDate,
	currentWeekOrders,
	onOrdersUpdated,
}) => {
	const [preview, setPreview] = useState<PreviewState>({
		loading: true,
		sourceOrders: [],
		nonConflictingOrders: [],
		conflicts: [],
	});
	const [processing, setProcessing] = useState(false);

	// Calculate date ranges
	const dateRanges = useMemo(() => {
		const [year, month, day] = weekStartDate.split("-").map(Number);
		const currentWeekStart = startOfWeek(new Date(year, month - 1, day), { weekStartsOn: 1 });
		const previousWeekStart = subDays(currentWeekStart, 7);
		const previousWeekEnd = addDays(previousWeekStart, 6);
		const currentWeekEnd = addDays(currentWeekStart, 6);

		return {
			previousWeekStart,
			previousWeekEnd,
			currentWeekStart,
			currentWeekEnd,
			previousWeekText: `${format(previousWeekStart, "MMM d")} - ${format(previousWeekEnd, "MMM d, yyyy")}`,
			currentWeekText: `${format(currentWeekStart, "MMM d")} - ${format(currentWeekEnd, "MMM d, yyyy")}`,
		};
	}, [weekStartDate]);

	// Load preview data
	const loadPreview = useCallback(async () => {
		setPreview((prev) => ({ ...prev, loading: true }));

		try {
			// Fetch previous week orders
			const previousWeekMOs = await productionPlanApi.getByDateRange(
				dateRanges.previousWeekStart.toISOString(),
				endOfWeek(dateRanges.previousWeekStart, { weekStartsOn: 1 }).toISOString()
			);

			if (previousWeekMOs.length === 0) {
				setPreview({
					loading: false,
					sourceOrders: [],
					nonConflictingOrders: [],
					conflicts: [],
				});
				return;
			}

			// Map source orders to target week dates
			const mappedSourceOrders = previousWeekMOs.map((mo) => {
				const originalStartTime = new Date(mo.startTime);
				const newStartTime = addDays(originalStartTime, 7);

				let newEndTime: Date | null = null;
				if (mo.endTime) {
					const originalEndTime = new Date(mo.endTime);
					newEndTime = addDays(originalEndTime, 7);
				}

				return {
					...mo,
					startTime: newStartTime.toISOString(),
					endTime: newEndTime ? newEndTime.toISOString() : null,
				};
			});

			// Find conflicts
			const conflicts: ConflictItem[] = [];
			const nonConflictingOrders: ManufacturingOrder[] = [];

			mappedSourceOrders.forEach((sourceOrder) => {
				const conflictingTarget = currentWeekOrders.find((targetOrder) => {
					// Same workcenter
					if (sourceOrder.workstation !== targetOrder.workstation) return false;

					// Check time overlap on same day
					const sourceStart = new Date(sourceOrder.startTime);
					const sourceEnd = sourceOrder.endTime ? new Date(sourceOrder.endTime) : null;
					const targetStart = new Date(targetOrder.startTime);
					const targetEnd = targetOrder.endTime ? new Date(targetOrder.endTime) : null;

					return doTimesOverlap(sourceStart, sourceEnd, targetStart, targetEnd);
				});

				if (conflictingTarget) {
					conflicts.push({
						sourceOrder,
						targetOrder: conflictingTarget,
						action: "keep", // Default to keeping existing
					});
				} else {
					nonConflictingOrders.push(sourceOrder);
				}
			});

			setPreview({
				loading: false,
				sourceOrders: mappedSourceOrders,
				nonConflictingOrders,
				conflicts,
			});
		} catch (error) {
			console.error("Error loading preview:", error);
			setPreview({
				loading: false,
				sourceOrders: [],
				nonConflictingOrders: [],
				conflicts: [],
			});
			onError("Failed to load preview data");
		}
	}, [dateRanges, currentWeekOrders, onError]);

	useEffect(() => {
		if (open) {
			loadPreview();
		}
	}, [open, loadPreview]);

	// Handle conflict action change
	const handleConflictActionChange = (index: number, action: "keep" | "replace") => {
		setPreview((prev) => ({
			...prev,
			conflicts: prev.conflicts.map((conflict, i) =>
				i === index ? { ...conflict, action } : conflict
			),
		}));
	};

	// Process the copy operation
	const handleCopy = async () => {
		setProcessing(true);

		try {
			// Collect orders to create (non-conflicting + replaced conflicts)
			const ordersToCreate: Omit<ManufacturingOrder, "id">[] = [];
			const ordersToDelete: number[] = [];

			// Add all non-conflicting orders
			preview.nonConflictingOrders.forEach((order) => {
				ordersToCreate.push({
					productId: order.productId,
					productName: order.productName,
					productItemNumber: order.productItemNumber,
					sku: order.sku,
					quantity: order.quantity,
					workstation: order.workstation,
					startTime: order.startTime,
					endTime: order.endTime,
				});
			});

			// Handle conflicts based on user choice
			preview.conflicts.forEach((conflict) => {
				if (conflict.action === "replace") {
					// Mark existing order for deletion
					ordersToDelete.push(conflict.targetOrder.id);
					// Add the source order to create
					ordersToCreate.push({
						productId: conflict.sourceOrder.productId,
						productName: conflict.sourceOrder.productName,
						productItemNumber: conflict.sourceOrder.productItemNumber,
						sku: conflict.sourceOrder.sku,
						quantity: conflict.sourceOrder.quantity,
						workstation: conflict.sourceOrder.workstation,
						startTime: conflict.sourceOrder.startTime,
						endTime: conflict.sourceOrder.endTime,
					});
				}
				// If "keep", we don't do anything - existing order stays
			});

			// Delete orders that are being replaced
			for (const id of ordersToDelete) {
				await productionPlanApi.delete(id);
			}

			// Create new orders
			if (ordersToCreate.length > 0) {
				await productionPlanApi.createBatch(ordersToCreate);
			}

			const replacedCount = ordersToDelete.length;
			const addedCount = preview.nonConflictingOrders.length;
			const keptCount = preview.conflicts.filter((c) => c.action === "keep").length;

			let message = `Successfully copied ${addedCount} order(s)`;
			if (replacedCount > 0) {
				message += `, replaced ${replacedCount} conflicting order(s)`;
			}
			if (keptCount > 0) {
				message += `, kept ${keptCount} existing order(s)`;
			}

			onSuccess(message);
			onOrdersUpdated();
			onClose();
		} catch (error: any) {
			console.error("Error processing copy:", error);
			const errorMessage =
				error?.response?.data?.message || "Failed to copy orders";
			onError(errorMessage);
		} finally {
			setProcessing(false);
		}
	};

	// Summary stats
	const stats = useMemo(() => {
		const toAdd = preview.nonConflictingOrders.length;
		const toReplace = preview.conflicts.filter((c) => c.action === "replace").length;
		const toKeep = preview.conflicts.filter((c) => c.action === "keep").length;
		const total = preview.sourceOrders.length;

		return { toAdd, toReplace, toKeep, total, conflicts: preview.conflicts.length };
	}, [preview]);

	return (
		<Dialog
			open={open}
			onClose={() => !processing && onClose()}
			maxWidth="lg"
			fullWidth
			PaperProps={{
				sx: { borderRadius: `${planner.radius}px`, boxShadow: planner.shadowDialog },
			}}
		>
			<DialogTitle>
				<Box display="flex" alignItems="center" gap={1}>
					<SwapHorizIcon sx={{ color: planner.navy }} />
					<Typography sx={{ ...planner.type.title, color: planner.navy }}>
						Copy from Previous Week
					</Typography>
				</Box>
			</DialogTitle>

			<DialogContent dividers>
				{preview.loading ? (
					<Box display="flex" justifyContent="center" alignItems="center" py={4}>
						<CircularProgress sx={{ color: planner.accent }} />
						<Typography sx={{ ...planner.type.body, color: planner.inkSecondary, ml: 2 }}>
							Loading preview...
						</Typography>
					</Box>
				) : preview.sourceOrders.length === 0 ? (
					<Alert
						severity="info"
						icon={false}
						sx={{
							...planner.type.body,
							borderRadius: `${planner.radiusSm}px`,
							border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
							bgcolor: planner.unplannedSoft,
							color: planner.inkSecondary,
						}}
					>
						No job orders found in the previous week ({dateRanges.previousWeekText})
						to copy.
					</Alert>
				) : (
					<Box>
						{/* Summary */}
						<Paper
							elevation={0}
							sx={{
								p: 2,
								mb: 3,
								bgcolor: planner.surfaceSubtle,
								border: `${planner.borderWidth}px solid ${planner.hairline}`,
								borderRadius: `${planner.radius}px`,
							}}
						>
							<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, mb: 1 }}>
								Copy Summary
							</Typography>
							<Box display="flex" gap={2} flexWrap="wrap">
								<Chip
									icon={<CheckCircleIcon />}
									label={`${stats.toAdd} to add`}
									variant="outlined"
									sx={statusChipSx("staffed")}
								/>
								{stats.conflicts > 0 && (
									<>
										<Chip
											icon={<WarningIcon />}
											label={`${stats.toReplace} to replace`}
											variant="outlined"
											sx={statusChipSx("partial")}
										/>
										<Chip
											icon={<ErrorIcon />}
											label={`${stats.toKeep} to keep (skip)`}
											variant="outlined"
											sx={statusChipSx("unplanned")}
										/>
									</>
								)}
								<Chip
									label={`${stats.total} total from previous week`}
									variant="outlined"
									sx={{
										...planner.type.metaStrong,
										borderRadius: `${planner.radiusSm}px`,
										borderColor: planner.hairlineStrong,
										color: planner.inkSecondary,
									}}
								/>
							</Box>
						</Paper>

						{/* Non-conflicting orders */}
						{preview.nonConflictingOrders.length > 0 && (
							<Box mb={3}>
								<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, mb: 1 }}>
									<CheckCircleIcon
										fontSize="small"
										sx={{ verticalAlign: "middle", mr: 1, color: planner.staffed }}
									/>
									Orders to Add ({preview.nonConflictingOrders.length})
								</Typography>
								<TableContainer
									component={Paper}
									elevation={0}
									sx={{
										border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
										borderRadius: `${planner.radius}px`,
									}}
								>
									<Table size="small">
										<TableHead>
											<TableRow
												sx={{
													bgcolor: planner.staffedSoft,
													"& .MuiTableCell-root": {
														...planner.labelCaps,
														color: planner.inkSecondary,
														borderBottomColor: planner.hairline,
													},
												}}
											>
												<TableCell>Product/SKU</TableCell>
												<TableCell>Workcenter</TableCell>
												<TableCell>Quantity</TableCell>
												<TableCell>Date</TableCell>
												<TableCell>Time</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{preview.nonConflictingOrders.map((order, index) => {
												const startTime = new Date(order.startTime);
												return (
													<TableRow
														key={index}
														sx={{ "& .MuiTableCell-root": { borderBottomColor: planner.hairline } }}
													>
														<TableCell sx={{ ...planner.type.body, color: planner.ink }}>
															{order.productName || order.sku || "Unknown"}
														</TableCell>
														<TableCell sx={{ ...planner.type.body, color: planner.inkSecondary }}>
															{order.workstation}
														</TableCell>
														<TableCell sx={monoCellSx}>{order.quantity.toLocaleString()}</TableCell>
														<TableCell sx={monoCellSx}>{format(startTime, "EEE, MMM d")}</TableCell>
														<TableCell sx={monoCellSx}>
															{format(startTime, "HH:mm")}
															{order.endTime &&
																` - ${format(new Date(order.endTime), "HH:mm")}`}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}

						{/* Conflicts */}
						{preview.conflicts.length > 0 && (
							<Box>
								<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink, mb: 1 }}>
									<WarningIcon
										fontSize="small"
										sx={{ verticalAlign: "middle", mr: 1, color: planner.partial }}
									/>
									Conflicts ({preview.conflicts.length}) - Choose what to do
								</Typography>
								<Alert
									severity="warning"
									icon={false}
									sx={{
										...planner.type.body,
										mb: 2,
										borderRadius: `${planner.radiusSm}px`,
										border: `${planner.borderWidth}px solid ${planner.partial}55`,
										bgcolor: planner.partialSoft,
										color: planner.partial,
									}}
								>
									The following orders conflict with existing orders in the target week.
									Choose whether to keep the existing order or replace it with the copied one.
								</Alert>
								<TableContainer
									component={Paper}
									elevation={0}
									sx={{
										border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
										borderRadius: `${planner.radius}px`,
									}}
								>
									<Table size="small">
										<TableHead>
											<TableRow
												sx={{
													bgcolor: planner.partialSoft,
													"& .MuiTableCell-root": {
														...planner.labelCaps,
														color: planner.inkSecondary,
														borderBottomColor: planner.hairline,
													},
												}}
											>
												<TableCell>Workcenter</TableCell>
												<TableCell>Existing Order</TableCell>
												<TableCell>Copied Order</TableCell>
												<TableCell>Action</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{preview.conflicts.map((conflict, index) => {
												const existingStart = new Date(conflict.targetOrder.startTime);
												const copiedStart = new Date(conflict.sourceOrder.startTime);
												return (
													<TableRow
														key={index}
														sx={{ "& .MuiTableCell-root": { borderBottomColor: planner.hairline } }}
													>
														<TableCell>
															<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
																{conflict.sourceOrder.workstation}
															</Typography>
														</TableCell>
														<TableCell>
															<Box>
																<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
																	{conflict.targetOrder.productName ||
																		conflict.targetOrder.sku ||
																		"Unknown"}
																</Typography>
																<Typography sx={monoCellSx}>
																	Qty: {conflict.targetOrder.quantity.toLocaleString()}
																</Typography>
																<Typography sx={{ ...monoCellSx, display: "block", color: planner.inkMuted }}>
																	{format(existingStart, "EEE HH:mm")}
																</Typography>
															</Box>
														</TableCell>
														<TableCell>
															<Box>
																<Typography sx={{ ...planner.type.bodyStrong, color: planner.ink }}>
																	{conflict.sourceOrder.productName ||
																		conflict.sourceOrder.sku ||
																		"Unknown"}
																</Typography>
																<Typography sx={monoCellSx}>
																	Qty: {conflict.sourceOrder.quantity.toLocaleString()}
																</Typography>
																<Typography sx={{ ...monoCellSx, display: "block", color: planner.inkMuted }}>
																	{format(copiedStart, "EEE HH:mm")}
																</Typography>
															</Box>
														</TableCell>
														<TableCell>
															<RadioGroup
																value={conflict.action}
																onChange={(e) =>
																	handleConflictActionChange(
																		index,
																		e.target.value as "keep" | "replace"
																	)
																}
																sx={{
																	"& .MuiRadio-root": { color: planner.inkMuted },
																	"& .MuiRadio-root.Mui-checked": { color: planner.accent },
																}}
															>
																<FormControlLabel
																	value="keep"
																	control={<Radio size="small" />}
																	label={
																		<Typography sx={{ ...planner.type.body, color: planner.ink }}>
																			Keep existing
																		</Typography>
																	}
																/>
																<FormControlLabel
																	value="replace"
																	control={<Radio size="small" />}
																	label={
																		<Typography sx={{ ...planner.type.body, color: planner.ink }}>
																			Replace with copied
																		</Typography>
																	}
																/>
															</RadioGroup>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</TableContainer>
							</Box>
						)}
					</Box>
				)}
			</DialogContent>

			<DialogActions sx={{ borderTop: `${planner.borderWidth}px solid ${planner.hairline}`, px: 3, py: 2 }}>
				<GhostButton size="small" onClick={onClose} disabled={processing}>
					Cancel
				</GhostButton>
				<PrimaryButton
					size="small"
					onClick={handleCopy}
					disabled={
						processing ||
						preview.loading ||
						(preview.nonConflictingOrders.length === 0 &&
							preview.conflicts.filter((c) => c.action === "replace").length === 0)
					}
					startIcon={
						processing ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<SwapHorizIcon sx={{ fontSize: 16 }} />
						)
					}
				>
					{processing
						? "Processing..."
						: `Copy ${stats.toAdd + stats.toReplace} Order(s)`}
				</PrimaryButton>
			</DialogActions>
		</Dialog>
	);
};

export default CopyWeekPreviewDialog;


